import type { AsPlainObject } from 'minisearch';

export interface SearchDoc {
    id: string;
    name: string;
    categoryId?: string;
    skuBarcodeTokens?: string;
}

export const SEARCH_TIER = {
    NAME: 'name',
    SUFFIX: 'suffix',
    SKU_BARCODE: 'skuBarcode',
    FUZZY: 'fuzzy',
} as const;

export type SearchTier = (typeof SEARCH_TIER)[keyof typeof SEARCH_TIER];

export type SearchTierDurationsMs = Record<SearchTier, number>;

export type SearchTierResultCounts = Record<SearchTier, number>;

export const SEARCH_WORKER_REQUEST = {
    HYDRATE: 'HYDRATE',
    ADD_BATCH: 'ADD_BATCH',
    UPSERT: 'UPSERT',
    REMOVE: 'REMOVE',
    QUERY: 'QUERY',
    PERSIST: 'PERSIST',
    CLEAR: 'CLEAR',
} as const;

export type SearchWorkerRequestType =
    (typeof SEARCH_WORKER_REQUEST)[keyof typeof SEARCH_WORKER_REQUEST];

export type SearchWorkerRequest =
    | {
          type: typeof SEARCH_WORKER_REQUEST.HYDRATE;
          requestId: string;
          serializedIndex: AsPlainObject | null;
      }
    | {
          type: typeof SEARCH_WORKER_REQUEST.ADD_BATCH;
          requestId: string;
          docs: SearchDoc[];
          offset: number;
      }
    | {
          type: typeof SEARCH_WORKER_REQUEST.UPSERT;
          requestId: string;
          docs: SearchDoc[];
      }
    | {
          type: typeof SEARCH_WORKER_REQUEST.REMOVE;
          requestId: string;
          ids: string[];
      }
    | {
          type: typeof SEARCH_WORKER_REQUEST.QUERY;
          requestId: string;
          term: string;
          categoryIds: string[];
      }
    | { type: typeof SEARCH_WORKER_REQUEST.PERSIST; requestId: string }
    | { type: typeof SEARCH_WORKER_REQUEST.CLEAR; requestId: string };

export const SEARCH_WORKER_RESPONSE = {
    HYDRATED: 'HYDRATED',
    ADD_BATCH_DONE: 'ADD_BATCH_DONE',
    UPSERT_DONE: 'UPSERT_DONE',
    REMOVE_DONE: 'REMOVE_DONE',
    QUERY_RESULT: 'QUERY_RESULT',
    CLEARED: 'CLEARED',
    PERSISTED: 'PERSISTED',
    ERROR: 'ERROR',
} as const;

export type SearchWorkerResponseType =
    (typeof SEARCH_WORKER_RESPONSE)[keyof typeof SEARCH_WORKER_RESPONSE];

type DistributiveOmit<
    TObject,
    KeyToOmit extends keyof TObject,
> = TObject extends unknown ? Omit<TObject, KeyToOmit> : never;

export type SearchWorkerRequestPayload = DistributiveOmit<
    SearchWorkerRequest,
    'requestId'
>;

export type SearchWorkerResponse =
    | {
          type: typeof SEARCH_WORKER_RESPONSE.HYDRATED;
          requestId: string;
          documentCount: number;
          buildOffset: number;
          readMs: number;
          deserializeMs: number;
      }
    | {
          type: typeof SEARCH_WORKER_RESPONSE.ADD_BATCH_DONE;
          requestId: string;
          documentCount: number;
      }
    | {
          type: typeof SEARCH_WORKER_RESPONSE.UPSERT_DONE;
          requestId: string;
          documentCount: number;
      }
    | {
          type: typeof SEARCH_WORKER_RESPONSE.REMOVE_DONE;
          requestId: string;
          documentCount: number;
      }
    | {
          type: typeof SEARCH_WORKER_RESPONSE.QUERY_RESULT;
          requestId: string;
          ids: string[];
          tierDurationsMs: SearchTierDurationsMs;
          tierResultCounts: SearchTierResultCounts;
      }
    | { type: typeof SEARCH_WORKER_RESPONSE.PERSISTED; requestId: string }
    | {
          type: typeof SEARCH_WORKER_RESPONSE.ERROR;
          requestId: string;
          message: string;
      }
    | { type: typeof SEARCH_WORKER_RESPONSE.CLEARED; requestId: string };
